/**
 * 时间轴业务语义（与 API getview.current_minute、柱图 view_minute 一致）
 *
 * current_minute：race_view_x 本场 MIN(min)，每次 getview 唯一时间点
 * entry_minute：前端首次进入本场时记录的最左刻度，刷新不再改
 *
 * 柱图滑块：time_points = DISTINCT min，有什么显示什么
 * 频谱表 UI 仍只画 -1~15
 *
 * 预跑：-1=開跑；正赛 0~20；隔夜等大 min 若表里有则出现在滑块上
 */

/** 隔夜 db min（柱图样式/孖宝用，与 RaceviewX3 一致） */
export const OVERNIGHT_DB_MINS = [30, 60, 120, 240, 360, 720, 999];

export function isOvernightDbMin(m) {
  const n = Number(m);
  if (Number.isNaN(n)) return false;
  return OVERNIGHT_DB_MINS.some((x) => Math.abs(n - x) < 0.001);
}

/** 柱图滑块：time_series 里出现的 db_min 均有效（有什么显示什么） */
export function isRaceViewXTimelineMinute(m) {
  if (m == null || Number.isNaN(Number(m))) return false;
  return Number(m) >= -1;
}

export function isRunMinute(m) {
  return Math.abs(Number(m) + 1) < 0.021;
}

export function minutesEqual(a, b) {
  return Math.abs(Number(a) - Number(b)) < 0.001;
}

/** API/DB min → time_series key 后缀，如 -0.5 → "-0.5"，-1 → "-1" */
export function formatTimelineMinLabel(m) {
  const n = Number(m);
  if (Number.isNaN(n)) return '';
  if (isRunMinute(n)) return '-1';
  if (n === 0) return '0';
  if (n > 0 && Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n));
  return String(n.toFixed(2)).replace(/\.?0+$/, '');
}

export function timeKeyForMinute(minute) {
  const n = Number(minute);
  if (Number.isNaN(n)) return null;
  if (Math.abs(n) < 0.001) return 'time_0';
  return `time_${formatTimelineMinLabel(n)}`;
}

export function parseTimeKeyMinute(key) {
  if (!key || !String(key).startsWith('time_')) return null;
  const raw = String(key).slice(5);
  const m = parseFloat(raw);
  return Number.isNaN(m) ? null : m;
}

/** 滑块/前后钮用：在已排序时间点中找下标 */
export function minuteIndex(points, minute) {
  if (!points?.length) return 0;
  const i = points.findIndex((p) => minutesEqual(p, minute));
  return i >= 0 ? i : 0;
}

/** 正赛缘分钟：[-1,0) 小数 + -1 開跑 + 0~20（直播 sync 缘用） */
export function isTimelineMinute(m) {
  if (m == null || Number.isNaN(Number(m))) return false;
  const n = Number(m);
  if (n > 20) return false;
  if (n < -1) return false;
  return true;
}

/** 柱图/滑块显示文案 */
export function formatTimelineMinuteLabel(m) {
  const n = Number(m);
  if (Number.isNaN(n)) return '';
  if (isRunMinute(n)) return '開跑';
  if (n > -1 && n < 0) return formatTimelineMinLabel(n);
  if (n === 0) return '0 min';
  if (n > 0 && n <= 20) return `${Math.round(n)} min`;
  if (isOvernightDbMin(n)) return `${Math.round(n)}`;
  return formatTimelineMinLabel(n) || String(n);
}

function hasSpectrumActivity(tsEntry) {
  if (!tsEntry?.spectrum) return false;
  const s = tsEntry.spectrum;
  return Number(s.spec_win) > 0 || Number(s.spec_qin) > 0 || Number(s.spec_qpl) > 0;
}

function hasMinuteActivity(tsEntry) {
  if (!tsEntry) return false;
  if (hasSpectrumActivity(tsEntry)) return true;
  const add = Number(tsEntry.add_value);
  if (!Number.isNaN(add) && add !== 0) return true;
  return false;
}

/** 该 time_key 在 time_series 中是否存在 */
function horseHasTimeMinute(horse, minute) {
  const key = timeKeyForMinute(minute);
  return !!(horse?.time_series && Object.prototype.hasOwnProperty.call(horse.time_series, key));
}

/**
 * 整数分钟需有前一分钟槽位，排除 DB 预写 time_20 而缺 18/19 的跳档占位
 */
function isReachableActivityMinute(horse, minute) {
  const m = Number(minute);
  if (isOvernightDbMin(m)) return true;
  if (!isTimelineMinute(minute)) return false;
  if (Number.isNaN(m) || m <= 0) return true;
  if (Math.abs(m - Math.round(m)) > 0.021) return true;
  const prev = Math.round(m) - 1;
  if (prev < -1) return true;
  return horseHasTimeMinute(horse, prev);
}

/** 单马：有实质数据且可达的最大分钟（含 add_value，用于展示缘） */
export function inferHorseMaxActivityMinute(horse) {
  let hm = -Infinity;
  for (const [key, val] of Object.entries(horse?.time_series || {})) {
    const m = parseTimeKeyMinute(key);
    if (!isTimelineMinute(m)) continue;
    if (!hasMinuteActivity(val)) continue;
    if (!isReachableActivityMinute(horse, m)) continue;
    if (m > hm) hm = m;
  }
  return hm > -Infinity ? hm : -Infinity;
}

export function inferSpectrumDisplayEdge(horses) {
  if (!horses?.length) return -1;
  let edge = -1;
  for (const h of horses) {
    for (const [key, val] of Object.entries(h.time_series || {})) {
      const m = parseTimeKeyMinute(key);
      if (!isTimelineMinute(m)) continue;
      if (hasMinuteActivity(val) && m > edge) edge = m;
    }
  }
  return edge;
}

/** 频谱表最右着色列（任马 spec_win/qin/qpl > 0） */
export function inferSpectrumVisualEdge(horses) {
  if (!horses?.length) return -1;
  let edge = -1;
  for (const h of horses) {
    for (const [key, val] of Object.entries(h.time_series || {})) {
      const m = parseTimeKeyMinute(key);
      if (!isTimelineMinute(m)) continue;
      if (hasSpectrumActivity(val) && m > edge) edge = m;
    }
  }
  return edge;
}

/** 可达活动缘：全场最大分钟（排除跳档占位），用于压低虚高的 API */
export function inferSpectrumReachableActivityEdge(horses) {
  if (!horses?.length) return -1;
  let edge = -1;
  for (const h of horses) {
    for (const [key, val] of Object.entries(h.time_series || {})) {
      const m = parseTimeKeyMinute(key);
      if (!isTimelineMinute(m)) continue;
      if (!hasMinuteActivity(val)) continue;
      if (!isReachableActivityMinute(h, m)) continue;
      if (m > edge) edge = m;
    }
  }
  return edge;
}

/** race_view_x 行是否有实质频谱/分值（排除仅占位、无着色的 min 行） */
function hasRaceViewXRowActivity(tsEntry) {
  if (!tsEntry) return false;
  if (hasSpectrumActivity(tsEntry)) return true;
  const add = Number(tsEntry.add_value);
  return !Number.isNaN(add) && add !== 0;
}

/**
 * 单马：race_view_x 中该马最大 min（db_min，有活动且可达）
 */
export function inferHorseMaxRaceViewXMin(horse) {
  let maxM = -Infinity;
  for (const val of Object.values(horse?.time_series || {})) {
    const dbm = Number(val?.db_min);
    if (!isTimelineMinute(dbm)) continue;
    if (!hasRaceViewXRowActivity(val)) continue;
    if (!isReachableActivityMinute(horse, dbm)) continue;
    if (dbm > maxM) maxM = dbm;
  }
  return maxM > -Infinity ? maxM : -Infinity;
}

/**
 * race_view_x 本场 MIN(min)，与 API current_minute 一致（兜底推断）
 */
export function inferRaceViewXSyncMinute(horsesOrOne) {
  return minViewMinute(collectRaceViewXTimePoints(horsesOrOne));
}

/** @deprecated 别名 */
export const inferRaceViewXCurrentMinute = inferRaceViewXSyncMinute;

/** @deprecated 别名，语义同 inferRaceViewXSyncMinute */
export function inferSpectrumSyncFrontier(horses) {
  return inferRaceViewXSyncMinute(horses);
}

export const inferSpectrumLiveMinute = inferRaceViewXSyncMinute;

function normalizeApiMinute(apiMinute) {
  const api = Number(apiMinute);
  if (Number.isNaN(api) || api === 999) return -1;
  return api;
}

export function reconcileCurrentMinute(apiMinute, horses, opts = {}) {
  const api = normalizeApiMinute(apiMinute);

  if (opts.viewSource === 'historical' || opts.historical === true) {
    return api;
  }

  if (opts.raceCompleted === true) {
    return api;
  }

  if (isTimelineMinute(api) && api >= -1) {
    return api;
  }

  const fromX = inferRaceViewXSyncMinute(horses);
  if (isTimelineMinute(fromX) && fromX >= -1) {
    return fromX;
  }

  return api;
}

/** 本场 race_view_x 全部 min（各马 time_series.db_min 去重排序） */
export function collectRaceViewXTimePoints(horsesOrOne) {
  const list = Array.isArray(horsesOrOne) ? horsesOrOne : [horsesOrOne];
  const set = new Set();
  for (const horse of list) {
    if (!horse?.time_series) continue;
    for (const [key, val] of Object.entries(horse.time_series)) {
      const dbm = Number(val?.db_min);
      if (!Number.isNaN(dbm) && isRaceViewXTimelineMinute(dbm)) {
        set.add(Number(dbm.toFixed(4)));
        continue;
      }
      const fromKey = parseTimeKeyMinute(key);
      if (fromKey != null && isRaceViewXTimelineMinute(fromKey)) {
        set.add(Number(fromKey.toFixed(4)));
      }
    }
  }
  return [...set].sort((a, b) => a - b);
}

/** 入场默认：本场 race_view_x 最小 min */
export function raceViewXEntryMinute(horsesOrOne) {
  const points = collectRaceViewXTimePoints(horsesOrOne);
  return minViewMinute(points);
}

export function collectAvailableTimePoints(horsesOrOne, opts = {}) {
  let points = collectRaceViewXTimePoints(horsesOrOne);
  const cap = opts.capAt;
  if (cap != null && isTimelineMinute(cap)) {
    const capN = Number(cap);
    points = points.filter((p) => p <= capN + 0.001);
  }
  return points;
}

export function buildSpectrumTimeColumns(horsesOrOne) {
  const points = collectAvailableTimePoints(horsesOrOne);
  if (!points.length) {
    const fallback = [];
    for (let m = -1; m <= 16; m += 1) {
      fallback.push({
        minute: m,
        key: timeKeyForMinute(m),
        label: m === -1 ? '-1' : String(m),
      });
    }
    return fallback;
  }
  return points.map((m) => ({
    minute: m,
    key: timeKeyForMinute(m),
    label: isRunMinute(m) ? '-1' : formatTimelineMinLabel(m),
  }));
}

export function maxViewMinute(points) {
  return points.length ? points[points.length - 1] : 20;
}

export function isRaceSessionEnded(raceMeta, viewSource, graceMinutes = 28) {
  if (viewSource === 'historical') return true;
  if (!raceMeta?.date || !raceMeta?.time) return false;
  const start = parseRaceStartMs(raceMeta.date, raceMeta.time);
  if (!start) return false;
  return Date.now() > start + graceMinutes * 60 * 1000;
}

function parseRaceStartMs(dateStr, timeStr) {
  const d = String(dateStr || '').trim();
  const t = String(timeStr || '').trim();
  if (!d || !t) return null;
  const norm = d.replace(/\//g, '-');
  const m = norm.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const tm = t.match(/^(\d{1,2}):(\d{2})/);
  if (!tm) return null;
  return new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(tm[1]),
    Number(tm[2]),
    0,
  ).getTime();
}

export function minViewMinute(points) {
  return points.length ? points[0] : -1;
}

export function defaultViewMinuteOnEntry(_currentMinute, availablePoints) {
  return minViewMinute(availablePoints);
}

export function clampViewMinute(target, currentMinute, availablePoints, opts = {}) {
  let v = Number(target);
  const min = minViewMinute(availablePoints);
  if (Number.isNaN(v)) v = min;
  const cur = Number(currentMinute);
  const capLive = opts.capToLive !== false;
  // 仅正赛分钟受 live 缘限制；隔夜 30+ 可拖到 time_series 任意点
  if (
    capLive
    && !Number.isNaN(cur)
    && Number.isFinite(cur)
    && isTimelineMinute(v)
    && isTimelineMinute(cur)
    && v > cur
  ) {
    v = cur;
  }
  if (!availablePoints.length) return v;
  if (availablePoints.some((p) => minutesEqual(p, v))) return v;
  const eligible = availablePoints.filter((p) => (
    p <= v && (!capLive || Number.isNaN(cur) || !Number.isFinite(cur) || p <= cur)
  ));
  if (eligible.length) return eligible[eligible.length - 1];
  return availablePoints[0];
}
